import { paths } from "./userinput/paths";
import { addComponent, removeComponent, hasComponent, defineQuery } from "bitecs";
import {
  Hovered,
  LongHoldRemoteRight,
  LongHoldRemoteLeft,
  Selected,
  Interactable,
  MovementThresholdActive
} from "../bit-components";

export class LongHoldSystem {
  constructor(scene) {
    this.LONG_HOLD_DURATION = 600;

    const GRAB_REMOTE_RIGHT = paths.actions.cursor.right.grab;
    const GRAB_REMOTE_LEFT = paths.actions.cursor.left.grab;
    const DROP_REMOTE_RIGHT = paths.actions.cursor.right.drop;
    const DROP_REMOTE_LEFT = paths.actions.cursor.left.drop;
    const DESELECT_REMOTE_RIGHT = paths.actions.cursor.right.deselect;
    const DESELECT_REMOTE_LEFT = paths.actions.cursor.left.deselect;

    this.hoverQuery = defineQuery([Interactable, Hovered]);

    this.inputMethods = [
      {
        grabPath: GRAB_REMOTE_RIGHT,
        dropPath: DROP_REMOTE_RIGHT,
        deselectPath: DESELECT_REMOTE_RIGHT,
        trackingQuery: defineQuery([LongHoldRemoteRight]),
        trackingComponent: LongHoldRemoteRight
      },
      {
        grabPath: GRAB_REMOTE_LEFT,
        dropPath: DROP_REMOTE_LEFT,
        deselectPath: DESELECT_REMOTE_LEFT,
        trackingQuery: defineQuery([LongHoldRemoteLeft]),
        trackingComponent: LongHoldRemoteLeft
      }
    ];

    this.selectedQuery = defineQuery([Selected]);
    this.movedQuery = defineQuery([MovementThresholdActive]);
    this.scene = scene;
    this.selectObject = this.selectObject.bind(this);
    this.deselectObject = this.deselectObject.bind(this);
    this.handleCameraMove = this.handleCameraMove.bind(this);
    this.handleInteraction = this.handleInteraction.bind(this);

    this.scene.addEventListener("camera-movement-detected", this.handleCameraMove);
    this.scene.addEventListener("interacted", this.handleInteraction);
  }

  handleCameraMove() {
    const selectedEntities = this.selectedQuery(APP.world);
    selectedEntities.forEach(eid => {
      this.deselectObject(APP.world, eid);
    });
  }

  handleInteraction() {
    const selectedEntities = this.selectedQuery(APP.world);
    selectedEntities.forEach(eid => {
      this.deselectObject(APP.world, eid);
    });
  }

  handleLongHold(world, hoveredEid) {
    if (hasComponent(world, Selected, hoveredEid)) {
      removeComponent(world, Selected, hoveredEid);
      return;
    }

    const existingMenus = this.selectedQuery(world);
    existingMenus.forEach(eid => {
      if (eid !== hoveredEid) {
        this.deselectObject(world, eid);
      }
    });

    this.selectObject(world, hoveredEid);
  }

  tick(world, userinput, dt) {
    const currentlySelected = this.selectedQuery(world);
    const movedSet = new Set(this.movedQuery(world));
    const hoveredEntities = this.hoverQuery(world);
    const hoveredSet = new Set(hoveredEntities);

    this.inputMethods.forEach(({ grabPath, dropPath, deselectPath, trackingQuery, trackingComponent }) => {
      const trackingEntities = trackingQuery(world);

      if (userinput.get(deselectPath)) {
        currentlySelected.forEach(eid => {
          this.deselectObject(world, eid);
        });
        return;
      }

      trackingEntities.forEach(eid => {
        if (!hoveredSet.has(eid)) {
          removeComponent(world, trackingComponent, eid);
        }
      });

      if (hoveredEntities.length === 0) return;

      const hoveredEid = hoveredEntities[0];
      const grabStarted = userinput.get(grabPath);
      const grabEnded = userinput.get(dropPath);
      const isSelected = hasComponent(world, Selected, hoveredEid);

      if (grabStarted && !hasComponent(world, trackingComponent, hoveredEid) && !isSelected) {
        currentlySelected.forEach(eid => {
          this.deselectObject(world, eid);
        });
        addComponent(world, trackingComponent, hoveredEid);
        trackingComponent.duration[hoveredEid] = 0;
      }

      if (hasComponent(world, trackingComponent, hoveredEid)) {
        if (grabEnded) {
          removeComponent(world, trackingComponent, hoveredEid);
          return;
        }

        if (movedSet.has(hoveredEid)) {
          removeComponent(world, trackingComponent, hoveredEid);
          return;
        }

        trackingComponent.duration[hoveredEid] += dt;

        if (trackingComponent.duration[hoveredEid] >= this.LONG_HOLD_DURATION) {
          this.handleLongHold(world, hoveredEid);
          removeComponent(world, trackingComponent, hoveredEid);
        }
      }
    });

    currentlySelected.forEach(eid => {
      if (movedSet.has(eid)) {
        this.deselectObject(world, eid);
      }
    });
  }

  selectObject(world, eid) {
    addComponent(world, Selected, eid);
    const object = APP.world.eid2obj.get(eid);
    if (object) {
      this.scene.emit("object-selected", { eid, object });
    }
  }

  deselectObject(world, eid) {
    removeComponent(world, Selected, eid);
    const object = APP.world.eid2obj.get(eid);
    if (object) {
      this.scene.emit("object-deselected", { eid });
    }
  }

  remove() {
    this.scene.removeEventListener("camera-movement-detected", this.handleCameraMove);
    this.scene.removeEventListener("interacted", this.handleInteraction);
  }
}
